/**
 * Mock Market Data Engine
 *
 * Simulates realistic market data for ~15 NGX equities, FX pairs, ETFs, and bond yields.
 * Each instrument ticks every 1-2 seconds with random walk + momentum bias.
 *
 * Used by MockBrokerClient and MockWsClient to provide realistic price feeds
 * and position mark-to-market updates.
 */

interface PricePoint {
  bid: number;
  ask: number;
  last: number;
  volume: number;
  timestamp: number;
}

export interface L2Data {
  symbol: string;
  bid: number;
  ask: number;
  bidSize: number;
  askSize: number;
  last: number;
  volume: number;
  timestamp: number;
}

export interface TickCallback {
  (data: L2Data): void;
}

// Constants
const TICK_INTERVAL = 1000 + Math.random() * 1000; // 1-2 seconds
const VOLATILITY = 0.0012; // daily volatility (0.12%)
const JUMP_PROBABILITY = 0.05; // 5% chance per tick
const SPREAD_BPS = { min: 5, max: 25 }; // 5-25 kobo basis points

// NGX Equities with realistic opening prices (₦)
const NGX_EQUITIES: Record<string, number> = {
  'ZENITHBANK': 32.50,
  'GTCO': 1205.00,
  'ACCESS': 11.20,
  'MTNN': 290.00,
  'AIRTELAFRI': 1580.00,
  'DANGCEM': 685.00,
  'BUACEMENT': 95.50,
  'NESTLE': 970.00,
  'NB': 5.45,
  'GUARANTY': 0.62,
  'FBNH': 11.80,
  'UBA': 41.50,
  'STERLING': 3.68,
  'OANDO': 8.25,
  'SEPLAT': 1680.00,
};

// FX pairs with USD base (starting at realistic NGN rates)
const FX_PAIRS: Record<string, number> = {
  'USD/NGN': 1550.00, // Nigerian Naira
  'ZAR/NGN': 85.00,   // South African Rand
  'EGP/NGN': 51.00,   // Egyptian Pound
  'KES/NGN': 12.00,   // Kenyan Shilling
};

// ETFs (lower volatility than equities)
const ETFS: Record<string, number> = {
  'VETGRIF30': 38.55,
  'VANGOF40': 42.10,
  'NXTINDX': 51.25,
  'ETFEMR15': 29.80,
  'ETFVALUE': 35.20,
};

// Bond clean prices (stored as prices, not yields, for consistency with positions)
// These represent clean price percentages (% of par)
const BONDS: Record<string, number> = {
  'FGN27-14.55': 98.75,   // Clean price 98.75% of par
  'FGN30-15.10': 97.20,   // Clean price 97.20% of par
  'FGN50-12.80': 94.50,   // Clean price 94.50% of par
  'FGN2026': 98.75,       // Alias for mock market data (yield: 12.80%)
  'FGN2030': 97.20,       // Alias for mock market data (yield: 14.20%)
  'FGN2050': 94.50,       // Alias for mock market data (yield: 13.80%)
};

// Volatility overrides per class
const CLASS_VOLATILITY: Record<string, number> = {
  'fx': 0.0025,      // FX more volatile
  'bond': 0.00008,   // Bonds very stable
  'etf': 0.0008,     // ETFs less volatile than equities
};

/**
 * MockMarketDataEngine tracks all instruments and their price evolution.
 * Clients subscribe to tick updates. The engine ticks on a timer.
 */
export class MockMarketDataEngine {
  private instruments: Map<string, PricePoint> = new Map();
  private subscribers: Map<string, Set<TickCallback>> = new Map();
  private randomSeeds: Map<string, number> = new Map();
  private tickInterval: ReturnType<typeof setInterval> | undefined;
  private tickCount = 0;

  constructor() {
    this.initializeInstruments();
    this.startTicker();
  }

  private initializeInstruments() {
    // Initialize equities
    Object.entries(NGX_EQUITIES).forEach(([sym, price]) => {
      this.instruments.set(sym, {
        bid: price - 0.025,
        ask: price,
        last: price,
        volume: 0,
        timestamp: Date.now(),
      });
      this.randomSeeds.set(sym, Math.random());
    });

    // Initialize FX pairs
    Object.entries(FX_PAIRS).forEach(([sym, price]) => {
      this.instruments.set(sym, {
        bid: price - 0.5,
        ask: price,
        last: price,
        volume: 0,
        timestamp: Date.now(),
      });
      this.randomSeeds.set(sym, Math.random());
    });

    // Initialize ETFs
    Object.entries(ETFS).forEach(([sym, price]) => {
      this.instruments.set(sym, {
        bid: price - 0.015,
        ask: price,
        last: price,
        volume: 0,
        timestamp: Date.now(),
      });
      this.randomSeeds.set(sym, Math.random());
    });

    // Initialize bonds (stored as yields in bps, not prices)
    Object.entries(BONDS).forEach(([sym, yield_bps]) => {
      this.instruments.set(sym, {
        bid: yield_bps - 2,
        ask: yield_bps,
        last: yield_bps,
        volume: 0,
        timestamp: Date.now(),
      });
      this.randomSeeds.set(sym, Math.random());
    });
  }

  private startTicker() {
    this.tickInterval = setInterval(() => {
      this.tickCount += 1;
      this.instruments.forEach((point, symbol) => {
        const updated = this.tickPrice(symbol, point);
        this.instruments.set(symbol, updated);

        // Emit to all subscribers for this symbol
        const subs = this.subscribers.get(symbol);
        if (subs && subs.size > 0) {
          const l2 = this.getL2Data(symbol);
          subs.forEach((cb) => cb(l2));
        }
      });
    }, TICK_INTERVAL);
  }

  private tickPrice(symbol: string, current: PricePoint): PricePoint {
    const vol = CLASS_VOLATILITY[this.getClass(symbol)] ?? VOLATILITY;
    const seed = this.randomSeeds.get(symbol) ?? Math.random();

    // Random walk: price * (1 + N(0, vol))
    const z = this.boxMullerZ(seed, this.tickCount);
    let change = 1 + z * vol;

    // Momentum bias: 5% chance of jump
    if (Math.random() < JUMP_PROBABILITY) {
      const jumpDir = Math.random() < 0.5 ? 1 : -1;
      change *= 1 + jumpDir * (0.005 + Math.random() * 0.01);
    }

    const newLast = current.last * change;
    const spread = Math.random() * (SPREAD_BPS.max - SPREAD_BPS.min) + SPREAD_BPS.min;
    const spreadFraction = spread / 10000; // Convert kobo to fraction

    return {
      bid: newLast * (1 - spreadFraction),
      ask: newLast,
      last: newLast,
      volume: this.generateVolume(symbol),
      timestamp: Date.now(),
    };
  }

  private getClass(symbol: string): string {
    if (symbol.includes('/')) return 'fx';
    if (symbol.startsWith('FGN') || symbol === 'TBILL') return 'bond';
    if (Object.keys(ETFS).includes(symbol)) return 'etf';
    return 'equity';
  }

  // Box-Muller transform for normal distribution
  private boxMullerZ(seed: number, tick: number): number {
    const u1 = Math.sin(seed * 12.9898 + tick * 78.233) * 43758.5453;
    const u1_frac = u1 - Math.floor(u1);
    const u2 = Math.sin(seed * 39.3456 + tick * 73.156) * 43758.5453;
    const u2_frac = u2 - Math.floor(u2);
    const r = Math.sqrt(-2 * Math.log(u1_frac));
    const theta = 2 * Math.PI * u2_frac;
    return r * Math.cos(theta);
  }

  private generateVolume(symbol: string): number {
    const isFx = symbol.includes('/');
    const isEtf = Object.keys(ETFS).includes(symbol);

    let volumeBase = 100_000;
    if (isFx) volumeBase = 5_000_000;
    if (isEtf) volumeBase = 500_000;

    const variance = volumeBase * (0.5 + Math.random());
    return Math.floor(variance);
  }

  /**
   * Get current L2 snapshot for a symbol
   */
  getL2Data(symbol: string): L2Data {
    const point = this.instruments.get(symbol);
    if (!point) throw new Error(`Symbol ${symbol} not found`);

    return {
      symbol,
      bid: point.bid,
      ask: point.ask,
      bidSize: Math.floor(Math.random() * 50000 + 10000),
      askSize: Math.floor(Math.random() * 50000 + 10000),
      last: point.last,
      volume: point.volume,
      timestamp: point.timestamp,
    };
  }

  /**
   * Get current price (mid) for a symbol
   */
  getPrice(symbol: string): number {
    const point = this.instruments.get(symbol);
    if (!point) return 0;
    return (point.bid + point.ask) / 2;
  }

  /**
   * Subscribe to tick updates for a symbol. Returns unsubscribe function.
   */
  subscribe(symbol: string, callback: TickCallback): () => void {
    if (!this.subscribers.has(symbol)) {
      this.subscribers.set(symbol, new Set());
    }
    this.subscribers.get(symbol)!.add(callback);

    return () => {
      const set = this.subscribers.get(symbol);
      if (set) {
        set.delete(callback);
        if (set.size === 0) {
          this.subscribers.delete(symbol);
        }
      }
    };
  }

  /**
   * Get list of all tracked symbols
   */
  getAllSymbols(): string[] {
    return Array.from(this.instruments.keys());
  }

  /**
   * Shut down the engine
   */
  shutdown() {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = undefined;
    }
  }
}

// Singleton instance
let engine: MockMarketDataEngine | undefined;

export function getOrCreateEngine(): MockMarketDataEngine {
  if (!engine) {
    engine = new MockMarketDataEngine();
  }
  return engine;
}

/**
 * Convenience helper: get price directly from the default engine
 */
export function getMockPrice(symbol: string): number {
  return getOrCreateEngine().getPrice(symbol);
}

/**
 * Convenience helper: subscribe to a symbol's tick updates
 */
export function subscribeMockPrice(symbol: string, callback: TickCallback): () => void {
  return getOrCreateEngine().subscribe(symbol, callback);
}
